import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAppSelector } from '@/app/hooks';
import type { TFunction } from 'i18next';
import type { Order, OrderStep } from '@/types/supervisor';
import { registerStepLeaveGuard } from '@/services/stepLeaveGuard';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import ItemSelectionHeader, { type ItemSelectionOption } from './ItemSelectionHeader';
import { Button } from '@/components/ui/button';
import { calculateOrderItemPricing, type OrderItemPricingRequest } from '@/services/pricingService';
import {
  getPricingPreviewLabel,
  getStoredPricingLabel,
  type PreviewPriceState,
} from '@/components/order/hooks/useDebouncedPricingPreview';
import { logging } from '@/services/logging';
import { getPrintQualityWarningLabel } from './utils/printQualityWarning';
import { selectOrderPricingCacheVersion } from '@/features/order/orderSlice';

export type PrintableOrderItem = Order['items'] extends (infer Item)[]
  ? Item extends { id: string }
    ? Item
    : Item & { id: string }
  : never;

export interface ItemStepPanelLayoutProps<TValue> {
  readonly t: TFunction;
  readonly availableItems: readonly PrintableOrderItem[];
  readonly items: readonly PrintableOrderItem[];
  readonly title: string;
  readonly emptyStateMessage: string;
  readonly uploadCtaLabel: string;
  readonly onUploadClick: () => void;
  readonly onExit: () => void;
  readonly enableUnsavedExitGuard?: boolean;
  readonly pristineSaveLabel?: string;
  readonly dirtySaveLabel?: string;
  readonly discardChangesTitle?: string;
  readonly discardChangesDescription?: string;
  readonly discardChangesConfirmText?: string;
  readonly discardChangesCancelText?: string;
  readonly resolveItemValue?: (item: PrintableOrderItem) => TValue | undefined;
  readonly areValuesEqual?: (left: TValue | undefined, right: TValue | undefined) => boolean;
  readonly onSave?: (itemId: string, value: TValue) => void;
  readonly onPersistComplete?: (savedItems: ReadonlyArray<{ itemId: string; value: TValue }>) => void;
  readonly isItemAssigned?: (item: PrintableOrderItem) => boolean;
  readonly resolvePricingPreviewRequest?: (
    item: PrintableOrderItem,
    value: TValue,
  ) => OrderItemPricingRequest | null;
  readonly renderWindow: (args: {
    item: PrintableOrderItem;
    onClose: () => void;
    onBack: () => void;
    onSave: (value: TValue) => void;
    onConfirmSelection: (value: TValue) => void;
    draftValue: TValue | undefined;
    setDraftValue: (value: TValue | undefined) => void;
    setDraftValidity: (isValid: boolean) => void;
    clearDraftValue: () => void;
    setDraftValueForAll: (value: TValue | undefined) => void;
    saveDisabled: boolean;
    saveLabel?: string;
  }) => ReactNode;
}

const PREVIEW_PRICE_DEBOUNCE_MS = 1_000;

export const filterPrintableItems = (order: Order): PrintableOrderItem[] => {
  const items = Array.isArray(order.items) ? order.items : [];
  return items.filter((item): item is PrintableOrderItem => typeof item?.id === 'string' && item.id.length > 0);
};

export const ItemStepPanelLayout = <TValue,>({
  t,
  availableItems,
  items,
  title,
  emptyStateMessage,
  uploadCtaLabel,
  onUploadClick,
  onExit,
  enableUnsavedExitGuard = false,
  pristineSaveLabel,
  dirtySaveLabel,
  discardChangesTitle,
  discardChangesDescription,
  discardChangesConfirmText,
  discardChangesCancelText,
  onSave,
  onPersistComplete,
  isItemAssigned,
  resolvePricingPreviewRequest,
  renderWindow,
  resolveItemValue,
  areValuesEqual,
}: ItemStepPanelLayoutProps<TValue>) => {
  const [activeIndex, setActiveIndex] = useState(() => (items.length > 0 ? 0 : -1));
  const [draftValues, setDraftValues] = useState<Map<string, TValue>>(() => new Map());
  const [invalidDraftItemIds, setInvalidDraftItemIds] = useState<Set<string>>(() => new Set());
  const [previewPrices, setPreviewPrices] = useState<Record<string, PreviewPriceState>>({});
  const pricingCacheVersion = useAppSelector(selectOrderPricingCacheVersion);
  const draftValuesRef = useRef<Map<string, TValue>>(new Map());
  const invalidDraftItemIdsRef = useRef<Set<string>>(new Set());
  const pendingLeaveResolverRef = useRef<((allowed: boolean) => void) | null>(null);
  const [discardPromptTarget, setDiscardPromptTarget] = useState<null | { source: 'back' } | { source: 'stepRibbon'; targetStep: OrderStep }>(null);
  const previewDebounceTimersRef = useRef<Map<string, number>>(new Map());
  const previewRequestIdRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    draftValuesRef.current = draftValues;
  }, [draftValues]);

  useEffect(() => {
    invalidDraftItemIdsRef.current = invalidDraftItemIds;
  }, [invalidDraftItemIds]);

  const valuesEqual = useCallback((left: TValue | undefined, right: TValue | undefined) => {
    if (typeof areValuesEqual === 'function') {
      return areValuesEqual(left, right);
    }
    return Object.is(left, right);
  }, [areValuesEqual]);

  const getResolvedItemValue = useCallback((item: PrintableOrderItem): TValue | undefined => {
    if (typeof resolveItemValue === 'function') {
      return resolveItemValue(item);
    }
    return undefined;
  }, [resolveItemValue]);

  const shouldStoreDraftValue = useCallback((item: PrintableOrderItem | null, value: TValue | undefined): value is TValue => {
    if (!item || typeof value === 'undefined') {
      return false;
    }
    const resolvedItemValue = getResolvedItemValue(item);
    const shouldStore = !valuesEqual(value, resolvedItemValue);
    return shouldStore;
  }, [getResolvedItemValue, valuesEqual]);

  const updatePreviewState = useCallback((itemId: string, next: PreviewPriceState | null) => {
    setPreviewPrices((prev) => {
      if (next == null) {
        if (!(itemId in prev)) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      }

      const current = prev[itemId];
      if (JSON.stringify(current) === JSON.stringify(next)) {
        return prev;
      }
      return {
        ...prev,
        [itemId]: next,
      };
    });
  }, []);

  const clearPreviewDebounce = useCallback((itemId: string) => {
    const timerId = previewDebounceTimersRef.current.get(itemId);
    if (timerId) {
      window.clearTimeout(timerId);
      previewDebounceTimersRef.current.delete(itemId);
    }
  }, []);

  useEffect(() => {
    return () => {
      previewDebounceTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      previewDebounceTimersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    previewDebounceTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    previewDebounceTimersRef.current.clear();
    previewRequestIdRef.current.clear();
    setPreviewPrices({});
  }, [pricingCacheVersion]);

  const queuePreviewPrice = useCallback((item: PrintableOrderItem, payload: OrderItemPricingRequest) => {
    const itemId = item.id;
    const requestId = (previewRequestIdRef.current.get(itemId) ?? 0) + 1;
    previewRequestIdRef.current.set(itemId, requestId);
    clearPreviewDebounce(itemId);

    updatePreviewState(itemId, { status: 'loading' });

    const timerId = window.setTimeout(async () => {
      previewDebounceTimersRef.current.delete(itemId);
      if (previewRequestIdRef.current.get(itemId) !== requestId) {
        return;
      }

      try {
        const result = await calculateOrderItemPricing(payload);
        if (previewRequestIdRef.current.get(itemId) !== requestId) {
          return;
        }
        updatePreviewState(itemId, {
          status: 'success',
          value: result.price.value,
          currency: result.price.currency,
        });
      } catch (error) {
        if (previewRequestIdRef.current.get(itemId) !== requestId) {
          return;
        }
        logging.error('[ItemStepPanelLayout] Failed to preview price', { error, itemId, payload });
        updatePreviewState(itemId, { status: 'failed' });
      }
    }, PREVIEW_PRICE_DEBOUNCE_MS);

    previewDebounceTimersRef.current.set(itemId, timerId);
  }, [clearPreviewDebounce, updatePreviewState]);

  useEffect(() => {
    if (items.length === 0) {
      setActiveIndex(-1);
      return;
    }

    setActiveIndex(previous => {
      if (previous < 0 || previous >= items.length) {
        return 0;
      }
      return previous;
    });
  }, [items]);

  const activeItem = useMemo(() => {
    if (activeIndex < 0 || activeIndex >= items.length) {
      return null;
    }
    return items[activeIndex] ?? null;
  }, [activeIndex, items]);

  const activeItemId = activeItem?.id ?? null;

  const activeDraftValue = useMemo(() => {
    if (!activeItemId) {
      return undefined;
    }
    return draftValues.get(activeItemId);
  }, [activeItemId, draftValues]);

  const setActiveDraftValue = useCallback(
    (value: TValue | undefined) => {
      if (!activeItemId) {
        return;
      }

      const previousValue = draftValuesRef.current.get(activeItemId);
      const shouldPersistDraft = shouldStoreDraftValue(activeItem, value);

      if (shouldPersistDraft && valuesEqual(previousValue, value)) {
        return;
      }
      if (!shouldPersistDraft && typeof previousValue === 'undefined') {
        return;
      }

      const next = new Map(draftValuesRef.current);
      if (!shouldPersistDraft) {
        next.delete(activeItemId);
      } else {
        next.set(activeItemId, value);
      }
      draftValuesRef.current = next;
      setDraftValues(next);

      if (!resolvePricingPreviewRequest || !activeItem) {
        return;
      }

      if (typeof value === 'undefined') {
        clearPreviewDebounce(activeItemId);
        updatePreviewState(activeItemId, null);
        return;
      }

      const payload = resolvePricingPreviewRequest(activeItem, value);
      if (!payload) {
        clearPreviewDebounce(activeItemId);
        updatePreviewState(activeItemId, null);
        return;
      }

      void queuePreviewPrice(activeItem, payload);
    },
    [activeItem, activeItemId, clearPreviewDebounce, queuePreviewPrice, resolvePricingPreviewRequest, shouldStoreDraftValue, updatePreviewState, valuesEqual],
  );

  const clearActiveDraftValue = useCallback(() => {
    if (!activeItemId) {
      return;
    }
    if (!draftValuesRef.current.has(activeItemId)) {
      return;
    }
    const next = new Map(draftValuesRef.current);
    next.delete(activeItemId);
    draftValuesRef.current = next;
    setDraftValues(next);
    setInvalidDraftItemIds(prev => {
      if (!prev.has(activeItemId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(activeItemId);
      invalidDraftItemIdsRef.current = next;
      return next;
    });
    clearPreviewDebounce(activeItemId);
    updatePreviewState(activeItemId, null);
  }, [activeItemId, clearPreviewDebounce, updatePreviewState]);

  const setActiveDraftValidity = useCallback((isValid: boolean) => {
    if (!activeItemId) {
      return;
    }

    setInvalidDraftItemIds(prev => {
      const isCurrentlyInvalid = prev.has(activeItemId);
      if (isValid && !isCurrentlyInvalid) {
        return prev;
      }
      if (!isValid && isCurrentlyInvalid) {
        return prev;
      }

      const next = new Set(prev);
      if (isValid) {
        next.delete(activeItemId);
      } else {
        next.add(activeItemId);
      }
      invalidDraftItemIdsRef.current = next;
      return next;
    });
  }, [activeItemId]);

  const setDraftValueForAll = useCallback(
    (value: TValue | undefined) => {
      if (typeof value === 'undefined') {
        if (draftValuesRef.current.size === 0) {
          return;
        }
        const empty = new Map<string, TValue>();
        draftValuesRef.current = empty;
        setDraftValues(empty);
      } else {
        const next = new Map(draftValuesRef.current);
        items.forEach((item) => {
          const identifier = item?.id;
          if (typeof identifier !== 'string' || identifier.length === 0) {
            return;
          }
          if (shouldStoreDraftValue(item, value)) {
            next.set(identifier, value);
            return;
          }
          next.delete(identifier);
        });
        draftValuesRef.current = next;
        setDraftValues(next);
      }

      setInvalidDraftItemIds(prev => {
        if (typeof value === 'undefined') {
          if (prev.size === 0) {
            return prev;
          }
          const empty = new Set<string>();
          invalidDraftItemIdsRef.current = empty;
          return empty;
        }
        return prev;
      });
    },
    [items, shouldStoreDraftValue],
  );

  useEffect(() => {
    if (draftValues.size === 0) {
      return;
    }

    const validIds = new Set<string>(availableItems.map(item => item.id));
    setDraftValues(prev => {
      let mutated = false;
      prev.forEach((_, key) => {
        if (!validIds.has(key)) {
          mutated = true;
        }
      });

      if (!mutated) {
        return prev;
      }

      const next = new Map<string, TValue>();
      prev.forEach((value, key) => {
        if (validIds.has(key)) {
          next.set(key, value);
        }
      });
      return next;
    });
  }, [availableItems, draftValues.size]);

  useEffect(() => {
    if (invalidDraftItemIds.size === 0) {
      return;
    }

    const validIds = new Set<string>(availableItems.map(item => item.id));
    setInvalidDraftItemIds(prev => {
      let mutated = false;
      prev.forEach((key) => {
        if (!validIds.has(key)) {
          mutated = true;
        }
      });

      if (!mutated) {
        return prev;
      }

      const next = new Set<string>();
      prev.forEach((key) => {
        if (validIds.has(key)) {
          next.add(key);
        }
      });
      invalidDraftItemIdsRef.current = next;
      return next;
    });
  }, [availableItems, invalidDraftItemIds.size]);

  const headerOptions = useMemo<readonly ItemSelectionOption[]>(() => {
    return items.map((item, index) => {
      const key = item?.id ?? `item-${index}`;
      const priceLabel = getStoredPricingLabel({ item, t });
      const previewPrice = previewPrices[key];
      const resolvedPriceLabel = previewPrice
        ? getPricingPreviewLabel({ previewPrice, fallback: item, t })
        : priceLabel;

      return {
        key,
        label: t('canvas.steps.itemNumber', 'Item {{index}}', { index: index + 1 }),
        uploadedFileId: item?.uploadedFileId ?? null,
        priceLabel: resolvedPriceLabel,
        priceLoading: previewPrice?.status === 'loading' || (!previewPrice && item.pricingStatus === 'loading'),
        warningLabel: getPrintQualityWarningLabel(item, t),
      } satisfies ItemSelectionOption;
    });
  }, [items, previewPrices, t]);

  const activeKey = headerOptions[activeIndex]?.key ?? null;
  const hasInvalidDrafts = invalidDraftItemIds.size > 0;
  const hasUnsavedDraftEntries = useCallback((): boolean => {
    return items.some((item) => {
      const identifier = item?.id;
      if (!identifier) {
        return false;
      }
      return shouldStoreDraftValue(item, draftValuesRef.current.get(identifier));
    });
  }, [items, shouldStoreDraftValue]);
  const hasUnsavedChanges = hasUnsavedDraftEntries();
  const resolvedSaveLabel = enableUnsavedExitGuard
    ? (hasUnsavedChanges ? dirtySaveLabel : pristineSaveLabel)
    : undefined;
  const resolvePendingLeave = useCallback((allowed: boolean) => {
    const resolver = pendingLeaveResolverRef.current;
    pendingLeaveResolverRef.current = null;
    if (resolver) {
      resolver(allowed);
    }
  }, []);

  const handleBack = useCallback(() => {
    const hasUnsaved = hasUnsavedDraftEntries();
    if (!enableUnsavedExitGuard || !hasUnsaved) {
      onExit();
      return;
    }

    setDiscardPromptTarget({ source: 'back' });
  }, [enableUnsavedExitGuard, hasUnsavedDraftEntries, onExit]);

  const handleDiscardPromptOpenChange = useCallback((open: boolean) => {
    if (open) {
      return;
    }

    if (discardPromptTarget?.source === 'stepRibbon') {
      resolvePendingLeave(false);
    }
    setDiscardPromptTarget(null);
  }, [discardPromptTarget, resolvePendingLeave]);

  const handleConfirmDiscard = useCallback(() => {
    const target = discardPromptTarget;
    setDiscardPromptTarget(null);

    if (target?.source === 'stepRibbon') {
      resolvePendingLeave(true);
      return;
    }

    onExit();
  }, [discardPromptTarget, onExit, resolvePendingLeave]);

  useEffect(() => {
    if (!enableUnsavedExitGuard) {
      return undefined;
    }

    return registerStepLeaveGuard(({ source, targetStep }) => {
      const hasUnsaved = hasUnsavedDraftEntries();
      if (source !== 'stepRibbon' || !hasUnsaved) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        pendingLeaveResolverRef.current = resolve;
        setDiscardPromptTarget({ source: 'stepRibbon', targetStep });
      });
    });
  }, [enableUnsavedExitGuard]);

  useEffect(() => () => {
    resolvePendingLeave(false);
  }, [resolvePendingLeave]);

  const handleSelectItem = useCallback(
    (key: string) => {
      const nextIndex = headerOptions.findIndex(option => option.key === key);
      if (nextIndex >= 0) {
        setActiveIndex(nextIndex);
      }
    },
    [headerOptions],
  );

  const persistSelections = useCallback(
    (value: TValue, draftSnapshot: Map<string, TValue>) => {
      if (invalidDraftItemIdsRef.current.size > 0) {
        return;
      }

      if (!onSave) {
        onExit();
        return;
      }

      const persisted = new Set<string>();
      const savedItems: Array<{ itemId: string; value: TValue }> = [];
      draftSnapshot.forEach((storedValue, itemId) => {
        if (typeof storedValue === 'undefined') {
          return;
        }
        onSave(itemId, storedValue);
        persisted.add(itemId);
        savedItems.push({ itemId, value: storedValue });
      });

      const targetId = activeItem?.id ?? null;
      if (targetId && !persisted.has(targetId)) {
        onSave(targetId, value);
        savedItems.push({ itemId: targetId, value });
      }

      if (draftSnapshot.size > 0) {
        draftValuesRef.current = new Map();
        setDraftValues(() => new Map());
      }

      onPersistComplete?.(savedItems);
      onExit();
    },
    [activeItem?.id, onExit, onPersistComplete, onSave],
  );

  const handleSave = useCallback(
    (value: TValue) => {
      persistSelections(value, new Map(draftValuesRef.current));
    },
    [persistSelections],
  );

  const handleConfirmSelection = useCallback(
    (value: TValue) => {
      if (!activeItem || activeIndex < 0 || activeIndex >= items.length || items.length === 0) {
        return;
      }

      const nextDraftValues = new Map(draftValuesRef.current);
      if (activeItem.id) {
        if (shouldStoreDraftValue(activeItem, value)) {
          nextDraftValues.set(activeItem.id, value);
        } else {
          nextDraftValues.delete(activeItem.id);
        }
      }

      setActiveDraftValue(value);

      if (items.length === 1) {
        persistSelections(value, nextDraftValues);
        return;
      }

      const isValueAssigned = (candidate: TValue | undefined): boolean => candidate != null;

      const isAssigned = (entry: PrintableOrderItem, index: number): boolean => {
        const identifier = entry?.id;
        if (!identifier) {
          return false;
        }
        if (index === activeIndex) {
          return isValueAssigned(value);
        }
        if (nextDraftValues.has(identifier)) {
          return isValueAssigned(nextDraftValues.get(identifier));
        }
        if (isItemAssigned) {
          return isItemAssigned(entry);
        }
        return false;
      };

      const findNextUnassignedIndex = (): number => {
        for (let i = activeIndex + 1; i < items.length; i += 1) {
          if (!isAssigned(items[i], i)) {
            return i;
          }
        }
        for (let i = 0; i < activeIndex; i += 1) {
          if (!isAssigned(items[i], i)) {
            return i;
          }
        }
        return -1;
      };

      const nextIndex = findNextUnassignedIndex();
      if (nextIndex === -1) {
        try {
          persistSelections(value, nextDraftValues);
        } catch (error) {
          logging.error('[ItemStepPanelLayout] handleSave failed during confirm selection', { error });
        }
        return;
      }

      setActiveIndex(nextIndex);
    },
    [activeIndex, activeItem, isItemAssigned, items, persistSelections, setActiveDraftValue, shouldStoreDraftValue],
  );

  if (availableItems.length === 0 || !activeItem) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold text-muted-foreground">{emptyStateMessage}</p>
        <Button onClick={onUploadClick}>{uploadCtaLabel}</Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <ItemSelectionHeader title={title} items={headerOptions} activeKey={activeKey} onSelect={handleSelectItem} />
        <div className="flex-1 min-h-0">
          {renderWindow({
            item: activeItem,
            onClose: onExit,
            onBack: handleBack,
            onSave: handleSave,
            onConfirmSelection: handleConfirmSelection,
            draftValue: activeDraftValue,
            setDraftValue: setActiveDraftValue,
            setDraftValidity: setActiveDraftValidity,
            clearDraftValue: clearActiveDraftValue,
            setDraftValueForAll,
            saveDisabled: hasInvalidDrafts,
            saveLabel: resolvedSaveLabel
                    })}
        </div>
      </div>
      <ConfirmAlertDialog
        open={discardPromptTarget != null}
        onOpenChange={handleDiscardPromptOpenChange}
        title={discardChangesTitle ?? 'Discard unsaved changes?'}
        description={discardChangesDescription ?? 'Your changes are not saved yet. You can keep editing or discard them and leave this screen.'}
        confirmText={discardChangesConfirmText ?? 'Discard changes'}
        cancelText={discardChangesCancelText ?? 'Keep editing'}
        onCancel={() => {
          if (discardPromptTarget?.source === 'stepRibbon') {
            resolvePendingLeave(false);
          }
          setDiscardPromptTarget(null);
        }}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
};

export default ItemStepPanelLayout;
